import Icon from '@/atoms/Icon/Icon';
import {StyledButton} from '../styles';
import {useRef, useState} from 'react';
import {StyledContainer, StyledLinkOption, StyledOptions, StyledButtonOption} from './styles';
import {ShareButtonProps} from './typings';
import useOutsideClick from '@/hooks/use-outside-click/use-outside-click';

const Option = ({title, icon, url, action, condition = true}) => {
  const Link = ({title, icon, url}) => (
    <StyledLinkOption title={title} target="_blank" rel="nofollow" href={url}>
      <Icon type={icon} fontSize={16} />
    </StyledLinkOption>
  );

  const Button = ({title, icon, action}) => (
    <StyledButtonOption title={title} onClick={action}>
      <Icon type={icon} fontSize={16} />
    </StyledButtonOption>
  );

  return condition ? (
    url ? (
      <Link title={title} icon={icon} url={url} />
    ) : action ? (
      <Button title={title} icon={icon} action={action} />
    ) : null
  ) : null;
};

const ShareButton = ({product}: ShareButtonProps) => {
  const [showOptions, setShowOptions] = useState(false);
  const shareOptions = useRef({
    facebook: {
      title: 'Share on Facebook',
      link: (url, imgUrl) =>
        `https://www.facebook.com/share.php?u=${encodeURIComponent(url)}&quote=${encodeURIComponent(
          'Check out this artwork on Artfinder: #artfinder #makingartwork',
        )}${imgUrl ? `&picture=${encodeURIComponent(imgUrl)}` : ''}`,
      icon: 'facebook',
    },
    whatsapp: {
      title: 'Share on WhatsApp',
      link: (url, imgUrl) =>
        `https://wa.me/?text=${encodeURIComponent(
          'Check out this artwork on Artfinder:',
        )}%20${encodeURIComponent(url)}`,
      icon: 'whatsapp',
    },
    x: {
      title: 'Share on X',
      link: (url, imgUrl) =>
        `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(
          'Check out this artwork on Artfinder:',
        )}&via=artfinder&hashtags=${'artfinder,makingartwork'}`,
      icon: 'twitter',
    },
    pinterest: {
      title: 'Share on Pinterest',
      link: (url, imgUrl) =>
        `https://pinterest.com/pin/create/button/?url=${encodeURIComponent(url)}${
          imgUrl ? `&media=${encodeURIComponent(imgUrl)}` : ''
        }&description=${'Check out this artwork on Artfinder:'}`,
      icon: 'pinterest',
    },
    copy: {
      title: 'Copy link to this artwork',
      action: async () => {
        try {
          await navigator.clipboard.writeText(window.location.href);
          // alert('Copied URL');
        } catch (e) {
          alert(`Couldn't copy to clipboard`);
        }
      },
      icon: 'link',
      condition: typeof navigator !== 'undefined' && !!navigator.clipboard,
    },
  });

  const toggleOptions = () => {
    setShowOptions(!showOptions);
  };

  const closeOptions = () => setShowOptions(false);

  const containerRef = useOutsideClick(closeOptions, showOptions);

  return (
    <StyledContainer ref={containerRef}>
      {showOptions && (
        <StyledOptions>
          {Object.keys(shareOptions.current).map((name, key) => {
            const data = shareOptions.current[name];

            return (
              <Option
                title={data.title}
                icon={data.icon}
                url={
                  data.link ? data.link(window.location.href, product?.images?.[0]?.url) : undefined
                }
                action={data.action}
                condition={data.condition}
                key={key}
              />
            );
          })}
        </StyledOptions>
      )}

      <StyledButton onClick={toggleOptions}>
        <Icon type="upload" fontSize={15} />

        <u>Share</u>
      </StyledButton>
    </StyledContainer>
  );
};

export default ShareButton;
